import type { LucideIcon } from "lucide-react"
import { useId } from "react"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/primitives/select.tsx"
import { Tabs, TabsList, TabsTrigger } from "@/primitives/tabs.tsx"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/primitives/tooltip.tsx"
import { cn } from "@/primitives/utils"
import { shortcutOf, type ShortcutId } from "@/shortcuts/registry.ts"
import { Kbd } from "./key.tsx"
import { segmentedFormOf } from "./segmented.ts"
import { useViewport } from "./viewport.tsx"

/*
 * One choice among a few, every choice in view: Spend's range, a model's Effort, the colour scheme.
 * It looks like the segmented Tabs, but it is one setting and has no panels, so a screen reader
 * hears a radio group ("Medium, radio button, 3 of 5, checked"); an arrow key moves the choice.
 */

/**
 * A choice's `label` is what its segment reads, after its `icon` when it has one; `longLabel`, when
 * set, is what it reads where the control is a Select, which has room for words ("Last 7 days" for
 * "7D"); `shortcut`, when
 * the page binds one to it, is named with its key in the segment's tooltip. `badge` counts what
 * waits behind the choice (a change request's proposals to review, its replies not yet read) in a
 * pill after the label, which `badge.label` says in words to a screen reader; a count of 0 shows
 * none.
 */
export type SegmentedOption<Value extends string> = {
  value: Value
  label: string
  longLabel?: string
  icon?: LucideIcon
  shortcut?: ShortcutId
  badge?: { count: number; label: string }
}

/** A choice's badge, when it counts anything. */
function CountBadge({ badge }: { badge: SegmentedOption<string>["badge"] }) {
  if (!badge?.count) return null
  return (
    <>
      <span
        aria-hidden
        className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-foreground px-1 text-2xs font-semibold text-background tabular-nums"
      >
        {badge.count}
      </span>
      <span className="sr-only">, {badge.label}</span>
    </>
  )
}

/**
 * `label` names the group for a screen reader. `disabledReason` shows the control as it stands but
 * takes no choice, and says why in a tooltip, as a model with no Effort to choose does; a segment
 * that takes the focus is described by the same reason, so a keyboard hears it too. A disabled
 * control can't be tapped, so on touch the reason is a line under it instead. `iconsOnCompact`
 * shows a segment that has an icon as its icon alone on `compact`, its label still read, where the
 * control shares a narrow row (the kitchen sink's header).
 *
 * On `compact` it fills the width it is given, each segment an equal share, and past four choices,
 * which a phone's width can't hold side by side, it is a Select of them instead, named by `label`.
 * `columnsOnCompact` sets its choices two to a row there instead, each segment 40px tall, for
 * choices that read as a few words each (a change request's Kind, four of them as a 2×2 grid).
 * `placeholder` is what that Select reads while none of its choices is chosen, as when the page
 * holds a choice the control doesn't offer (Insights' own dates: "Custom"); it reads as the value
 * in force, not as a prompt. `asSelect` draws the Select on the desk too, at its content's width,
 * where the row has no room (Insights' range in a narrow header). Docked as a sheet, the Select is
 * titled by `label`.
 */
export function Segmented<Value extends string>({
  label,
  value,
  options,
  onChange,
  disabledReason,
  iconsOnCompact = false,
  columnsOnCompact,
  placeholder,
  asSelect,
}: {
  label: string
  value: Value | null
  options: readonly SegmentedOption<Value>[]
  onChange: (value: Value) => void
  disabledReason?: string
  iconsOnCompact?: boolean
  columnsOnCompact?: 2
  placeholder?: string
  asSelect?: boolean
}) {
  const disabled = disabledReason !== undefined
  const reason = useId()
  const { compact } = useViewport()
  const form = segmentedFormOf({ compact, choices: options.length, columnsOnCompact, asSelect })
  const control =
    form === "select" ? (
      <Select
        items={options.map((option) => ({ ...option, label: option.longLabel ?? option.label }))}
        value={value}
        disabled={disabled}
        onValueChange={(next) => {
          const chosen = options.find((option) => option.value === next)
          if (chosen && !disabled) onChange(chosen.value)
        }}
      >
        <SelectTrigger
          aria-label={label}
          aria-describedby={disabled ? reason : undefined}
          // On the desk it stands among small buttons, at their height.
          size={compact ? "default" : "sm"}
          className={cn(compact && "w-full", "data-placeholder:text-foreground")}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent title={label}>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.icon && <option.icon aria-hidden />}
              {option.longLabel ?? option.label}
              <CountBadge badge={option.badge} />
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    ) : (
      <Tabs
        value={value}
        className="compact:w-full"
        onValueChange={(next: unknown) => {
          const chosen = options.find((option) => option.value === next)
          if (chosen && !disabled) onChange(chosen.value)
        }}
      >
        <TabsList
          variant="segmented"
          // A grid's track is as tall as its rows, and each segment a step taller than a row's, so
          // a segment's 44px hit area on touch stops at the gap rather than reaching the next row.
          className={form === "grid" ? "grid h-auto! w-full grid-cols-2 gap-1" : "compact:w-full"}
          role="radiogroup"
          aria-label={label}
          aria-disabled={disabled || undefined}
          activateOnFocus
        >
          {options.map((option) => {
            const segment = (
              <TabsTrigger
                key={option.value}
                value={option.value}
                disabled={disabled}
                role="radio"
                aria-checked={option.value === value}
                aria-selected={undefined}
                aria-describedby={disabled ? reason : undefined}
                className={form === "grid" ? "h-10!" : undefined}
              >
                {option.icon && <option.icon aria-hidden />}
                {option.icon && iconsOnCompact ? (
                  <span className="compact:sr-only">{option.label}</span>
                ) : (
                  option.label
                )}
                <CountBadge badge={option.badge} />
              </TabsTrigger>
            )
            if (!option.shortcut || disabled) return segment
            return (
              <Tooltip key={option.value}>
                <TooltipTrigger render={segment} />
                <TooltipContent>
                  {shortcutOf(option.shortcut).label}
                  <Kbd shortcut={option.shortcut} />
                </TooltipContent>
              </Tooltip>
            )
          })}
        </TabsList>
      </Tabs>
    )
  if (!disabled) return control
  // A disabled segment takes no pointer, so the tooltip hangs on the control around it.
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span className="inline-flex w-fit touch:flex-col touch:items-start touch:gap-1 compact:w-full" />
        }
      >
        {control}
        <span
          id={reason}
          className="sr-only touch:not-sr-only touch:text-xs touch:text-muted-foreground"
        >
          {disabledReason}
        </span>
      </TooltipTrigger>
      <TooltipContent>{disabledReason}</TooltipContent>
    </Tooltip>
  )
}
